import { Injectable, inject } from '@angular/core';
import { BaseAppSyncService } from '../base-app-sync.service';
import { Observable } from 'rxjs';
import {
  CreateOrUpdateStationLog,
  StationLog,
  StationLogConnection,
} from '../../model/station-logs/station-log';
import { StationLogReason } from '../../model/station-logs/station-log-reason';
import { getStationLogReasons } from '../../graphql/station-logs/get-station-log-reasons';
import { StationLogBuilderService } from '../builder/station-log-builder.service';
import { queryForStationLogs } from '../../graphql/station-logs/query-for-station-logs';
import { createStationLog } from '../../graphql/station-logs/create-station-log';
import { updateStationLog } from '../../graphql/station-logs/update-station-log';
import { deleteStationLog } from '../../graphql/station-logs/delete-station-log';
import {
  CreateOrEditPart,
  Part,
  PartConnection,
  PartSortOrQuery,
} from '../../model/station-logs/part';
import { createPart } from '../../graphql/station-logs/create-part';
import { editPart } from '../../graphql/station-logs/edit-part';
import { deletePart } from '../../graphql/station-logs/delete-part';
import { queryForParts } from '../../graphql/station-logs/query-for-parts';
import {
  CreateOrUpdatePlcAlarmCode,
  PlcAlarmCode,
  PlcAlarmCodeConnection,
  PlcAlarmCodeSortOrQuery,
} from '../../model/station-logs/plc-alarm-code';
import { queryForPlcAlarmCodes } from '../../graphql/station-logs/query-for-plc-alarm-codes';
import { Audit } from '../../model/common/audit';
import { getStationLogHistories } from '../../graphql/station-logs/get-station-log-history';
import { buildAuditHistory } from '../../core/functions/common-functions';
import { createPlcAlarmCode } from '../../graphql/station-logs/create-plc-alarm-code';
import { updatePlcAlarmCode } from '../../graphql/station-logs/edit-plc-alarm-code';
import { deletePlcAlarmCode } from '../../graphql/station-logs/delete-plc-alarm-code';

@Injectable({
  providedIn: 'root',
})
export class StationLogApiService extends BaseAppSyncService {
  private _stationLogBuilder = inject(StationLogBuilderService);

  getStationLogReasons(): Observable<StationLogReason[]> {
    return this.query(
      getStationLogReasons,
      {},
      (items: any[]) =>
        items.map((i) => this._stationLogBuilder.buildStationLogReason(i)),
      'getStationLogReasons'
    );
  }

  queryForStationLogs(
    limit: number,
    page: number,
    query: any,
    sort: any
  ): Observable<StationLogConnection> {
    return this.query(
      queryForStationLogs,
      { limit, page, query, sort },
      (response: any) => {
        return {
          items: response.items.map((i: any) =>
            this._stationLogBuilder.buildStationLog(i)
          ),
          totalRecords: response.totalRecords,
        };
      },
      'queryForStationLogs'
    );
  }

  createStationLog(input: CreateOrUpdateStationLog): Observable<StationLog> {
    return this.mutate(
      createStationLog,
      { input },
      (i: any) => this._stationLogBuilder.buildStationLog(i),
      'createStationLog'
    );
  }

  updateStationLog(input: CreateOrUpdateStationLog): Observable<StationLog> {
    return this.mutate(
      updateStationLog,
      { input },
      (i: any) => this._stationLogBuilder.buildStationLog(i),
      'updateStationLog'
    );
  }

  deleteStationLog(id: string): Observable<any> {
    return this.mutate(
      deleteStationLog,
      { id },
      (i: any) => i.value,
      'deleteStationLog'
    );
  }

  getStationLogHistories(id: string): Observable<Audit[]> {
    return this.query(
      getStationLogHistories,
      { id },
      (i: any) => buildAuditHistory(i),
      'getStationLogHistories'
    );
  }

  createPart(input: CreateOrEditPart): Observable<Part> {
    return this.mutate(
      createPart,
      { input },
      (i: any) => this._stationLogBuilder.buildPart(i),
      'createPart'
    );
  }

  editPart(id: string, input: CreateOrEditPart): Observable<Part> {
    return this.mutate(
      editPart,
      { id, input },
      (i: any) => this._stationLogBuilder.buildPart(i),
      'editPart'
    );
  }

  deletePart(id: String): Observable<any> {
    return this.mutate(deletePart, { id }, (i: any) => i, 'deletePart');
  }

  queryForParts(
    page: number,
    limit: number,
    query: PartSortOrQuery,
    sort: PartSortOrQuery
  ): Observable<PartConnection> {
    return this.query(
      queryForParts,
      { page, limit, query, sort },
      (response: any) => {
        return {
          items: response.items.map((value: any) =>
            this._stationLogBuilder.buildPart(value)
          ),
          totalRecords: response.totalRecords,
        };
      },
      'queryForParts'
    );
  }

  queryForPlcAlarmCodes(
    page: number,
    limit: number,
    query: PlcAlarmCodeSortOrQuery,
    sort: PlcAlarmCodeSortOrQuery
  ): Observable<PlcAlarmCodeConnection> {
    return this.query(
      queryForPlcAlarmCodes,
      { page, limit, query, sort },
      (response: any) => {
        return {
          items: response.items.map((value: any) =>
            this._stationLogBuilder.buildPlcAlarmCode(value)
          ),
          totalRecords: response.totalRecords,
        };
      },
      'queryForPlcAlarmCodes'
    );
  }

  createPlcAlarmCode(
    input: CreateOrUpdatePlcAlarmCode
  ): Observable<PlcAlarmCode> {
    return this.mutate(
      createPlcAlarmCode,
      { input },
      (i: any) => this._stationLogBuilder.buildPlcAlarmCode(i),
      'createPlcAlarmCode'
    );
  }

  updatePlcAlarmCode(
    id: string,
    input: CreateOrUpdatePlcAlarmCode
  ): Observable<PlcAlarmCode> {
    return this.mutate(
      updatePlcAlarmCode,
      { id, input },
      (i: any) => this._stationLogBuilder.buildPlcAlarmCode(i),
      'updatePlcAlarmCode'
    );
  }

  deletePlcAlarmCode(id: String): Observable<any> {
    return this.mutate(
      deletePlcAlarmCode,
      { id },
      (i: any) => i,
      'deletePlcAlarmCode'
    );
  }
}
